import { db } from "config/firebase";
import { createMD5hash } from "utils/createMD5hash";
import { doc, setDoc, getDoc, writeBatch } from "firebase/firestore";

export const createWhiteblacklist = async (hostId: string): Promise<void> => {
  await setDoc(doc(db, "whiteblacklist", hostId), {
    whitelist: {},
    blacklist: {},
  });
};

export const addPlayerToWhitelist = async (
  hostId: string,
  playerId: string,
  playerName: string,
): Promise<void> => {
  const batch = writeBatch(db);

  // add to whiteblacklist document
  const hostWBListRef = doc(db, "whiteblacklist", hostId);
  const wbListDocSnap = await getDoc(hostWBListRef);
  if (wbListDocSnap.exists()) {
    const whitelist = { ...wbListDocSnap.data().whitelist };
    const playerHash = createMD5hash(playerId);
    whitelist[playerHash] = true;
    batch.update(hostWBListRef, { whitelist });
  }

  // add to whitelist in host doc
  const hostRef = doc(db, "hosts", hostId);
  const hostDocSnap = await getDoc(hostRef);
  if (hostDocSnap.exists()) {
    const whitelist = { ...hostDocSnap.data().whitelist };
    whitelist[playerId] = playerName;
    batch.update(hostRef, { whitelist });
  }

  await batch.commit();
};

export const removeFromWhitelist = async (
  hostId: string,
  playerId: string,
): Promise<void> => {
  const batch = writeBatch(db);

  // remove from whiteblacklist document
  const hostWBListRef = doc(db, "whiteblacklist", hostId);
  const wbListDocSnap = await getDoc(hostWBListRef);
  if (wbListDocSnap.exists()) {
    const whitelist = { ...wbListDocSnap.data().whitelist };
    const playerHash = createMD5hash(playerId);
    delete whitelist[playerHash];
    batch.update(hostWBListRef, { whitelist });
  }

  // remove from whitelist in host doc
  const hostRef = doc(db, "hosts", hostId);
  const hostDocSnap = await getDoc(hostRef);
  if (hostDocSnap.exists()) {
    const whitelist = { ...hostDocSnap.data().whitelist };
    delete whitelist[playerId];
    batch.update(hostRef, { whitelist });
  }

  await batch.commit();
};

// used by player when sending a request
export const isWhitelisted = async (
  hostId: string,
  playerId: string,
): Promise<boolean> => {
  const hostWBListRef = doc(db, "whiteblacklist", hostId);
  const docSnap = await getDoc(hostWBListRef);

  if (docSnap.exists()) {
    const playerHash = createMD5hash(playerId);
    return docSnap.data().whitelist[playerHash] || false;
  }
  throw new Error("whiteblacklist doc does not exist");
};

export const isPlayerOnWhitelist = async (
  hostId: string,
  playerId: string,
): Promise<string | undefined> => {
  const hostRef = doc(db, "hosts", hostId);
  const docSnap = await getDoc(hostRef);

  if (docSnap.exists()) {
    const whitelist = { ...docSnap.data().whitelist };
    return whitelist[playerId];
  }
  throw new Error("host document not found");
};

export const addPlayerToBlacklist = async (
  hostId: string,
  playerId: string,
  playerName: string,
): Promise<void> => {
  const batch = writeBatch(db);

  // add to blacklist document
  const hostWBListRef = doc(db, "whiteblacklist", hostId);
  const wbListDocSnap = await getDoc(hostWBListRef);
  if (wbListDocSnap.exists()) {
    const blacklist = { ...wbListDocSnap.data().blacklist };
    const playerHash = createMD5hash(playerId);
    blacklist[playerHash] = true;
    batch.update(hostWBListRef, { blacklist });
  }

  // add to blacklist in host doc
  const hostRef = doc(db, "hosts", hostId);
  const hostDocSnap = await getDoc(hostRef);
  if (hostDocSnap.exists()) {
    const blacklist = { ...hostDocSnap.data().blacklist };
    blacklist[playerId] = playerName;
    batch.update(hostRef, { blacklist });
  }

  await batch.commit();
};

export const removeFromBlacklist = async (
  hostId: string,
  playerId: string,
): Promise<void> => {
  const batch = writeBatch(db);

  // remove from whiteblacklist document
  const hostWBListRef = doc(db, "whiteblacklist", hostId);
  const wbListDocSnap = await getDoc(hostWBListRef);
  if (wbListDocSnap.exists()) {
    const blacklist = { ...wbListDocSnap.data().blacklist };
    const playerHash = createMD5hash(playerId);
    delete blacklist[playerHash];
    batch.update(hostWBListRef, { blacklist });
  }

  // remove from blacklist in host doc
  const hostRef = doc(db, "hosts", hostId);
  const hostDocSnap = await getDoc(hostRef);
  if (hostDocSnap.exists()) {
    const blacklist = { ...hostDocSnap.data().blacklist };
    delete blacklist[playerId];
    batch.update(hostRef, { blacklist });
  }

  await batch.commit();
};

// used by player when sending a request
export const isBlacklisted = async (
  hostId: string,
  playerId: string,
): Promise<boolean> => {
  const hostWBListRef = doc(db, "whiteblacklist", hostId);
  const docSnap = await getDoc(hostWBListRef);

  if (docSnap.exists()) {
    const playerHash = createMD5hash(playerId);
    return docSnap.data().blacklist[playerHash] || false; // true or false
  }
  throw new Error("whiteblacklist doc does not exist");
};

export const isPlayerOnBlacklist = async (
  hostId: string,
  playerId: string,
): Promise<string | undefined> => {
  const hostRef = doc(db, "hosts", hostId);
  const docSnap = await getDoc(hostRef);

  if (docSnap.exists()) {
    const blacklist = { ...docSnap.data().blacklist };
    return blacklist[playerId];
  }
  throw new Error("host document not found");
};

export default {
  addPlayerToWhitelist,
  removeFromWhitelist,
  isWhitelisted,
  isPlayerOnWhitelist,
  addPlayerToBlacklist,
  removeFromBlacklist,
  isPlayerOnBlacklist,
  isBlacklisted,
};
